import type { btnPointInter } from '@/pages/home/utils/buriedPoint';

export interface IReportButtonLogParams {
  buttonId: string;
  buttonName?: string;
  uidType?: string;
  uid?: string;
  extInfo?: string;
}

interface IAuthPathRpcRequest {
  type?: string;
  payload?: unknown;
  requestId?: unknown;
}

interface ICreateAuthPathRpcHandlerOptions {
  openid?: string;
  typeId: string;
  getUserInfo: () => Promise<unknown>;
  reportButtonLog: (params: Omit<btnPointInter, 'pageId'>) => void;
}

const postResponse = (source: MessageEventSource | null, origin: string, message: Record<string, unknown>) => {
  (source as WindowProxy | null)?.postMessage(message, origin);
};

const getRequestId = (requestId: unknown): string | undefined => {
  if (typeof requestId === 'string') {
    return requestId;
  }
  return undefined;
};

const getStringField = (params: Record<string, unknown>, key: keyof IReportButtonLogParams): string | undefined => {
  const value = params?.[key];
  if (typeof value === 'string') {
    return value;
  }
  return undefined;
};

const validateButtonLogParams = (payload: unknown): { params?: IReportButtonLogParams; error?: string } => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { error: 'params must be an object' };
  }

  const payloadParams = payload as Record<string, unknown>;
  const buttonId = getStringField(payloadParams, 'buttonId');
  if (!buttonId?.trim()) {
    return { error: 'buttonId must be non-empty strings' };
  }

  const optionalKeys: Array<keyof IReportButtonLogParams> = ['buttonName', 'uidType', 'uid', 'extInfo'];
  const hasInvalidOptionalField = optionalKeys.some((key) => {
    return payloadParams?.[key] !== undefined && typeof payloadParams?.[key] !== 'string';
  });
  if (hasInvalidOptionalField) {
    return { error: 'optional params must be strings' };
  }

  return {
    params: {
      buttonId,
      buttonName: getStringField(payloadParams, 'buttonName'),
      uidType: getStringField(payloadParams, 'uidType'),
      uid: getStringField(payloadParams, 'uid'),
      extInfo: getStringField(payloadParams, 'extInfo'),
    },
  };
};

export const createAuthPathRpcHandler = (params: ICreateAuthPathRpcHandlerOptions) => {
  const { openid = '', typeId, getUserInfo, reportButtonLog } = params || {};

  return async (event: MessageEvent) => {
    const request = event.data as IAuthPathRpcRequest | undefined;
    if (!request || typeof request !== 'object') {
      return;
    }

    const requestId = getRequestId(request.requestId);
    if (request?.type === 'getOpenId') {
      postResponse(event?.source, event?.origin, {
        type: 'getOpenIdResponse',
        payload: openid,
        requestId,
      });
      return;
    }

    if (request?.type === 'getUserInfo') {
      try {
        const userInfo = await getUserInfo();
        postResponse(event?.source, event?.origin, {
          type: 'getUserInfoResponse',
          payload: userInfo || {},
          requestId,
        });
      } catch (e) {
        postResponse(event?.source, event?.origin, {
          type: 'getUserInfoResponse',
          payload: {
            success: false,
            error: 'get user info failed',
          },
          requestId,
        });
      }
      return;
    }

    if (request?.type === 'reportButtonLog') {
      const { params, error } = validateButtonLogParams(request?.payload);
      if (!params || error) {
        postResponse(event?.source, event?.origin, {
          type: 'reportButtonLogResponse',
          payload: {
            success: false,
            error,
          },
          requestId,
        });
        return;
      }

      reportButtonLog({
        ...params,
        extInfo: params?.extInfo || '',
        typeId,
      });
    }
  };
};
